import AdminPermission from '../../siteadmin/models/AdminPermission';
import AdminPermissionType from '../../siteadmin/models/AdminPermissionType';
import AuthService from './AuthService';
import Config from '../config';
import Feature from '../models/Feature';
import User from '../../membership/models/User';
import UserFeatureAccessPermissions from '../models/UserFeatureAccessPermissions';
import axios from 'axios';
import { isEmpty } from 'lodash';

class FeatureAccessService {
  private readonly dashboard: Feature = {
    name: 'Dashboard',
    key: 'dashboard',
    path: '/dashboard'
  };
  private readonly resources: Feature = {
    name: 'Resources',
    key: 'resources',
    path: '/resources'
  };
  private readonly report: Feature = {
    name: 'Reports',
    key: 'reporting',
    path: '/reporting'
  };
  private readonly groups: Feature = {
    name: 'Manage',
    key: 'usergroup',
    path: '/usergroup'
  };
  private readonly linkedAccounts: Feature = {
    name: 'Linked Accounts',
    key: 'linkedaccounts',
    path: '/linkedaccounts'
  };
  private readonly content: Feature = {
    name: 'Create',
    key: 'content',
    path: '/content'
  };
  private readonly design: Feature = {
    name: 'Design',
    key: 'design',
    path: '/design'
  };
  private readonly siteAdmin: Feature = {
    name: 'Site Admin',
    key: 'siteadmin',
    path: '/siteadmin'
  };
  private readonly notifications: Feature = {
    name: 'Notifications',
    key: 'notifications',
    path: '/notifications'
  };
  private readonly assessments: Feature = {
    name: 'Assessments',
    key: 'assessments',
    path: '/assessments'
  };

  private _loaded: boolean = false;
  private _adminPermissions: AdminPermission[];
  private _hasListGroupAccess: boolean;
  private _hasReportAccess: boolean;
  private _hasLinkedAccountsAccess: boolean;
  private _hasAuthoringAccess: boolean;
  private _hasMarkingAccess: boolean;

  /* Order of tabs based on site permissions */
  private readonly featureLandingOrder: Feature[] = [
    this.report,
    this.groups,
    this.assessments,
    this.content,
    this.dashboard
  ];

  public get AvailableFeatures() {
    const features: Feature[] = [this.dashboard, this.resources];
    if (this.hasLinkedAccountsAccess) {
      features.push(this.linkedAccounts);
    }
    if (this.hasMarkerAccess) {
      features.push(this.assessments);
    }
    if (this.hasReportAccess) {
      features.push(this.report);
    }
    if (this.hasGroupAccess || this._hasListGroupAccess) {
      features.push(this.groups);
    }
    if (this.hasAuthorAccess) {
      features.push(this.content);
    }
    if (this.hasDesignAccess) {
      features.push(this.design);
    }
    if (this.hasSiteAccess) {
      features.push(this.siteAdmin);
    }

    features.push(this.notifications);

    return features;
  }

  public async initAsync(user: User) {
    if (this._loaded) {
      return;
    }
    // called just after main App is instantiated
    // to check what features current user has access to.
    this._adminPermissions = this.getAdminPermissions(user);
    const featurePermissions = await this.getFeaturePermissions();
    this._hasListGroupAccess = featurePermissions?.listGroups;
    this._hasReportAccess = featurePermissions?.reporting;
    this._hasLinkedAccountsAccess =
      user?.parentkey == null &&
      user?.verifiedemail &&
      featurePermissions?.linkedAccounts;
    this._hasAuthoringAccess = featurePermissions?.authoring;
    this._hasMarkingAccess = featurePermissions?.marking;
    this._loaded = true;
  }

  public get hasMasterAccess(): boolean {
    return this.hasAccess('MASTER');
  }

  public get hasGroupAccess(): boolean {
    return this.hasAccess('GROUP');
  }

  public get hasReportAccess(): boolean {
    return this.hasAccess('MASTER') || this._hasReportAccess;
  }

  public get hasDesignAccess(): boolean {
    return this.hasAccess('DESIGN');
  }

  public get hasSiteAccess(): boolean {
    return this.hasAccess('SITE');
  }

  public get hasSuperAuthorAccess(): boolean {
    return this.hasAccess('AUTHOR');
  }

  public get hasAuthorAccess(): boolean {
    return this.hasAccess('AUTHOR') || this._hasAuthoringAccess;
  }

  public get hasMasterMarkerAccess(): boolean {
    return this.hasAccess('MARKER');
  }

  public get hasMarkerAccess(): boolean {
    return this.hasMasterMarkerAccess || this._hasMarkingAccess;
  }

  public get hasLinkedAccountsAccess(): boolean {
    return this._hasLinkedAccountsAccess && this.hasSiteAccess;
  }

  private hasAccess = (type?: AdminPermissionType) => {
    if (type == null) {
      // not restricted with access code
      return true;
    }

    const permissions = this._adminPermissions;
    if (this.hasPermission(permissions, 'MASTER')) {
      return true;
    }

    return this.hasPermission(permissions, type);
  };

  private hasPermission(
    permissions: AdminPermission[],
    type: AdminPermissionType
  ) {
    if (isEmpty(permissions)) {
      return false;
    }

    return permissions.find((p) => p.key === type)?.value;
  }

  private getAdminPermissions(user: User) {
    return [
      {
        key: 'MASTER',
        value: user?.masteraccess
      },
      {
        key: 'GROUP',
        value: user?.groupaccess
      },
      {
        key: 'DESIGN',
        value: user?.designaccess
      },
      {
        key: 'SITE',
        value: user?.siteaccess
      },
      {
        key: 'AUTHOR',
        value: user?.authoraccess
      },
      {
        key: 'MARKER',
        value: user?.markeraccess
      }
    ] as AdminPermission[];
  }

  private async getFeaturePermissions() {
    try {
      const accessToken = AuthService.getAccessToken();
      const { data } = await axios.get<UserFeatureAccessPermissions>(
        `${Config.MEMBERSHIP_DS}/api/featureaccess`,
        { headers: { authorization: `Bearer ${accessToken}` } }
      );

      return data;
    } catch {
      return null;
    }
  }

  public getLandingPath() {
    const features = this.AvailableFeatures;
    return this.featureLandingOrder.find((feature) =>
      features.includes(feature)
    )?.path;
  }
}

export default new FeatureAccessService();
